import { useEffect, useState } from "react";
import { TableLoaderSmall } from "../../../components/LoaderC";
import styles from "./TimeTracking.module.css";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Tooltip } from "@mui/material";
import { formatCurrencyDecimal } from "../../../helpers/currency";
import { WEEKLY_REGULAR_LIMIT, calcWeeklyPay, formatMinutesToHM, isWeekend } from "./weeklyPay";
import { Avatar, EntryDetailModal, EntryFormModal } from "./EntryModals";
import WeeklySummaryModal from "./WeeklySummaryModal";

dayjs.extend(customParseFormat);

const fmtTime = (t) => t ? dayjs(t, 'HH:mm:ss').format('hh:mm A') : null;

export const EmployeeCell = ({ item, onOpenEmployee }) => (
    <button type="button" className={`${styles.empBtn}`} title="View employee details" onClick={(e) => { e.stopPropagation(); onOpenEmployee(item); }}>
        <Avatar employee={item} size={36} />
        <span className={`${styles.empText}`}>
            <span className={`${styles.empName}`}>{item?.name}</span>
            <span className={`${styles.empRole}`}>{item?.role}</span>
        </span>
    </button>
);

export const EmptyState = () => (
    <div className={`${styles.panelEmpty}`}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: "#ccc", marginBottom: 10 }} aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
            <line x1="4" y1="4" x2="20" y2="20" />
        </svg>
        <div className={`${styles.emptyTitle}`}>Time tracking records unavailable.</div>
        <div className={`${styles.emptySub}`}>Time entries will appear here once tracking starts.</div>
    </div>
);

const DayCell = ({ emplyeeItem, item, setIsModalOpen, setIsNewModalOpen, setSelectedEmp, setSelectedDay, setEditData }) => {
    if (!item?.checkin_time) {
        return <button className={`${styles.dayBtn} ${styles.dayAdd}`} onClick={() => { setIsNewModalOpen(true); setSelectedEmp(emplyeeItem); setSelectedDay(item); setEditData(null); }} aria-label="Add time">
            <span className={`${styles.dayAddIcon}`}>+</span>ADD
        </button>
    }

    const inProgress = !item?.checkout_time;
    const cls = isWeekend(item?.date) ? styles.dayOt : item?.late ? styles.dayLate : styles.dayReg;
    const sub = inProgress ? (dayjs(item?.date).isSame(dayjs(), 'day') ? 'In progress' : 'No clock-out') : item?.late ? `Late ${item?.late}` : null;

    const tip = (<span className={`${styles.tipBody}`}>
        <span className={`${styles.tipRow}`}><span className={`${styles.tipLbl}`}>Start</span><b>{fmtTime(item?.checkin_time)}</b></span>
        <span className={`${styles.tipRow}`}><span className={`${styles.tipLbl}`}>End</span><b>{inProgress ? '—' : fmtTime(item?.checkout_time) + (item?.is_overnight ? ' (+1d)' : '')}</b></span>
    </span>);

    return (<Tooltip title={tip} arrow placement="top">
        <button className={`${styles.dayBtn} ${cls}`} onClick={() => { setIsModalOpen(true); setSelectedEmp(emplyeeItem); setSelectedDay(item); }}>
            <span className={`${styles.dayDur}`}>{formatMinutesToHM(item?.working_time, '0m')}</span>
            {sub && <span className={`${styles.daySub}`}>{sub}</span>}
        </button>
    </Tooltip>);
}

const WeekViewTable = ({ contentLoading, filteredData, dateRange, setLoading, updateTimeTracking, deleteTimeTracking, onOpenEmployee }) => {
    const [dayLabels, setDayLabels] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [editData, setEditData] = useState(null);
    const [summaryEmp, setSummaryEmp] = useState(null);

    useEffect(() => {
        const startDate = dayjs(dateRange?.start);
        const endDate = dayjs(dateRange?.end);

        const dates = [];

        let current = startDate;

        while (current.isBefore(endDate) || current.isSame(endDate, "day")) {
            dates.push(current.format("YYYY-MM-DD"));
            current = current.add(1, "day");
        }

        setDayLabels(dates);
    }, [dateRange]);

    return (<div className={`${styles.tblWrap}`}>
        {contentLoading && <div className="Loader TableLoader"><TableLoaderSmall /></div>}

        {!contentLoading && filteredData?.length > 0 && <table className={`${styles.table} ${styles.weekTable}`}>
            <thead>
                <tr>
                    <th className={`${styles.thEc}`}>Employee</th>
                    {dayLabels.map((d, i) => (
                        <th key={i} className={`${styles.th} ${d === dayjs().format('YYYY-MM-DD') ? styles.thToday : ''}`}>
                            <span className={`${styles.thDay}`}>
                                <span>{dayjs(d).format('ddd, MMM D')}</span>
                                {isWeekend(d) && <span className={`${styles.pillWe}`}>WEEKEND · OT</span>}
                            </span>
                        </th>
                    ))}
                    <th className={`${styles.th}`}>Regular</th>
                    <th className={`${styles.th}`}>Overtime</th>
                    <th className={`${styles.th} ${styles.thRight}`}>Wage</th>
                </tr>
            </thead>
            <tbody>
                {filteredData.map((item, index) => {
                    const pay = calcWeeklyPay(item);
                    const currency = item?.currency || 'USD';
                    const otNote = [
                        pay.weekendOtMinutes > 0 ? `Weekend ${formatMinutesToHM(pay.weekendOtMinutes)}` : null,
                        pay.weeklyOtMinutes > 0 ? `Weekly ${formatMinutesToHM(pay.weeklyOtMinutes)}` : null,
                    ].filter(Boolean).join(' · ') || 'No OT this week';

                    return <tr key={index} className={`${styles.tbodyTr}`}>
                        <td className={`${styles.tdEc}`}>
                            <EmployeeCell item={item} onOpenEmployee={onOpenEmployee} />
                        </td>
                        {item?.timeTracking.map((subItem, indd) => (<td key={indd} className={`${styles.td} ${dayjs(subItem?.date).isSame(dayjs(), 'day') ? styles.todayBg : ''}`}>
                            <DayCell emplyeeItem={item} item={subItem} setIsModalOpen={setIsModalOpen} setIsNewModalOpen={setIsNewModalOpen} setSelectedEmp={setSelectedEmp} setSelectedDay={setSelectedDay} setEditData={setEditData} />
                        </td>))}
                        <td className={`${styles.td}`}>
                            <button type="button" className={`${styles.totBtn}`} title="View weekly summary" onClick={() => setSummaryEmp(item)}>
                                <span className={`${styles.totVal}`}>{formatMinutesToHM(pay.regularMinutes, '0h')}</span>
                                <span className={`${styles.bar}`}><span className={`${styles.barFill}`} style={{ width: `${Math.min(100, (pay.regularMinutes / WEEKLY_REGULAR_LIMIT) * 100)}%` }} /></span>
                                <span className={`${styles.totNote}`}>{pay.weekdayMinutes >= WEEKLY_REGULAR_LIMIT ? 'cap reached' : 'of 40h'}</span>
                            </button>
                        </td>
                        <td className={`${styles.td}`}>
                            <button type="button" className={`${styles.totBtn}`} title="View weekly summary" onClick={() => setSummaryEmp(item)}>
                                <span className={`${styles.totVal} ${pay.otMinutes > 0 ? styles.textOt : ''}`}>{formatMinutesToHM(pay.otMinutes)}</span>
                                <span className={`${styles.totNote}`}>{otNote}</span>
                            </button>
                        </td>
                        <td className={`${styles.td} ${styles.tdRight}`}>
                            <div className={`${styles.wageVal}`}>{pay.wageTotal > 0 ? formatCurrencyDecimal(pay.wageTotal, currency) : '—'}</div>
                            {pay.otWage > 0 && <div className={`${styles.wageNote}`}>(OT {formatCurrencyDecimal(pay.otWage, currency)})</div>}
                        </td>
                    </tr>
                })}
            </tbody>
        </table>}

        {!contentLoading && filteredData?.length === 0 && <EmptyState />}

        {isModalOpen && (<EntryDetailModal setIsModalOpen={setIsModalOpen} selectedEmp={selectedEmp} selectedDay={selectedDay} deleteTimeTracking={deleteTimeTracking} setIsNewModalOpen={setIsNewModalOpen} setEditData={setEditData} />)}

        {isNewModalOpen && (<EntryFormModal setIsModalOpen={setIsNewModalOpen} selectedEmp={selectedEmp} selectedDay={selectedDay} setLoading={setLoading} updateTimeTracking={updateTimeTracking} editData={editData} />)}

        {summaryEmp && (<WeeklySummaryModal employee={summaryEmp} dateRange={dateRange} onClose={() => setSummaryEmp(null)} />)}
    </div>)
}

export default WeekViewTable;
